import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataGrid } from "@/components/viewer/DataGrid";
import { GridToolbar } from "@/components/viewer/GridToolbar";
import { DuplicatesBanner } from "@/components/viewer/DuplicatesBanner";
import { EMPTY_FILTERS, activeFilterCount, filterRows, findDuplicates, prepareRows, sortRows } from "@/lib/rows";
import { postDownload } from "@/lib/files";

export const Viewer = ({ baseUrl, filename }) => {
  const [all, setAll] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [removed, setRemoved] = useState(() => new Set());
  const [onlyDup, setOnlyDup] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    axios.get(`${baseUrl}/transactions`).then((r) => setAll(prepareRows(r.data))).catch(() => setLoadError(true));
  }, [baseUrl]);

  useEffect(load, [load]);

  const active = useMemo(() => (all ? all.filter((r) => !removed.has(r.idx)) : []), [all, removed]);
  const dups = useMemo(() => findDuplicates(active), [active]);
  const showOnlyDup = onlyDup && dups.nGroups > 0;
  const rows = useMemo(() => {
    let base = filterRows(active, filters);
    if (showOnlyDup) {
      base = base.filter((r) => dups.info.has(r.idx));
      if (!sort) base = [...base].sort((a, b) => dups.info.get(a.idx).key.localeCompare(dups.info.get(b.idx).key) || a.idx - b.idx);
    }
    return sortRows(base, sort);
  }, [active, filters, sort, showOnlyDup, dups]);
  const nFilters = activeFilterCount(filters) + (showOnlyDup ? 1 : 0);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const onSort = (col) => setSort((s) => (s?.col !== col ? { col, dir: "asc" } : s.dir === "asc" ? { col, dir: "desc" } : null));
  const removeRow = (idx) => setRemoved((s) => new Set([...s, idx]));
  const removeAllDups = () => {
    const extra = [...dups.info].filter(([, d]) => d.pos > 0).map(([idx]) => idx);
    setRemoved((s) => new Set([...s, ...extra]));
    setOnlyDup(false);
    toast.success(`${extra.length} ${extra.length === 1 ? "duplicato rimosso" : "duplicati rimossi"}`);
  };
  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    setOnlyDup(false);
  };

  const onExport = async (format) => {
    setExporting(format);
    try {
      const { data } = await axios.get(baseUrl);
      if (data.status !== "done") throw new Error("not ready");
    } catch {
      toast.error("Download non riuscito: la conversione potrebbe essere scaduta. Ricarica i PDF.");
      setExporting(null);
      return;
    }
    const indices = nFilters || sort || removed.size ? rows.map((r) => r.idx) : null;
    const suffix = `${removed.size ? "_senza_duplicati" : ""}${nFilters ? "_filtrato" : ""}`;
    postDownload(`${baseUrl}/export-file`, { format, indices: indices ? JSON.stringify(indices) : "", name: `${filename}${suffix}` });
    toast.success(`Download ${format === "xlsx" ? "Excel" : "CSV"} avviato: controlla la cartella Download.`);
    setTimeout(() => setExporting(null), 1500);
  };

  if (loadError) {
    return (
      <div data-testid="viewer-error" className="mt-8 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-slate-700">
        Impossibile caricare i movimenti (la conversione potrebbe essere scaduta).
        <Button size="sm" variant="outline" data-testid="viewer-retry" onClick={load}>Riprova</Button>
      </div>
    );
  }

  if (!all) {
    return (
      <div data-testid="viewer-loading" className="flex items-center gap-2 py-16 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Caricamento tabella…
      </div>
    );
  }

  return (
    <div data-testid="viewer" className="mt-8">
      <DuplicatesBanner dups={dups} removedCount={removed.size} onlyDup={showOnlyDup} setOnlyDup={setOnlyDup}
        onRemoveAll={removeAllDups} onRestore={() => setRemoved(new Set())} />
      <GridToolbar rows={rows} total={active.length} nFilters={nFilters} onClear={clearAll}
        onExport={onExport} exporting={exporting} />
      <DataGrid rows={rows} dupInfo={dups.info} onRemove={removeRow} filters={filters} setFilter={setFilter} sort={sort} onSort={onSort} />
    </div>
  );
};
